"use client";

import Link from "next/link";
import DownloadAppButtons from "@/components/download-app-buttons";
import { useWaitListStore } from "@/store/use-whaitlist.store";

import TextShimmer from "@cohub42/ui/components/magicui/animated-shiny-text";
import BoxReveal from "@cohub42/ui/components/magicui/box-reveal";
import { Iphone } from "@cohub42/ui/components/magicui/iphone";
import WordRotate from "@cohub42/ui/components/magicui/word-rotate";
import { Button } from "@cohub42/ui/components/ui/button";
import { Spotlight } from "@cohub42/ui/components/ui/spotlight";
import { cn } from "@cohub42/ui/lib/utils";

export default function HeroSection() {
  const { setShowModal } = useWaitListStore();

  return (
    <div className="bg-grid-white/[0.02] relative flex min-h-screen w-full overflow-hidden rounded-md bg-white/[0.96] antialiased dark:bg-black/[0.96]">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-blue-950/20 dark:via-black dark:to-purple-950/20" />

      <Spotlight
        className="-top-40 left-0 md:-top-20 md:left-60"
        fill="#3B82F6"
      />
      <Spotlight
        className="-top-40 right-0 md:-top-20 md:right-60"
        fill="#8B5CF6"
      />

      <div className="relative z-10 mx-auto flex max-w-7xl flex-col items-center justify-center px-4 py-32 text-center md:py-32">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-8">
          {/* Left Content */}
          <div className="flex flex-col items-center justify-center lg:items-start lg:text-left">
            <div
              className={cn(
                "group mx-auto mb-6 max-w-max rounded-full border border-black/5 bg-neutral-100 px-4 py-1.5 text-sm text-white transition-all ease-in hover:cursor-pointer hover:bg-neutral-200 dark:border-white/5 dark:bg-neutral-900 dark:hover:bg-neutral-800 lg:mx-0",
              )}
            >
              <TextShimmer className="inline-flex items-center justify-center transition ease-out hover:text-neutral-600 hover:duration-300 hover:dark:text-neutral-400">
                <span>🚀 Find Your Perfect Co-Founder</span>
              </TextShimmer>
            </div>

            <BoxReveal boxColor={"transparent"} duration={0.2}>
              <h1 className="relative z-10 bg-gradient-to-b from-slate-900 to-zinc-500 bg-clip-text text-center font-sans text-4xl font-bold leading-tight text-transparent dark:from-neutral-200 dark:to-neutral-600 md:text-5xl lg:text-left lg:text-6xl">
                Connect with Your
                <br />
                Future Co-Founder
              </h1>
            </BoxReveal>

            <BoxReveal boxColor={"transparent"} duration={0.3} width="100%">
              <p className="relative z-10 mx-auto mt-6 max-w-xl text-lg text-neutral-600 dark:text-neutral-400 lg:mx-0 lg:text-left">
                Join a growing community of entrepreneurs finding their perfect
                business partner on CoHub42. Swipe through AI-curated matches,
                join meetups, access startup resources, and get guidance from
                our AI mentor — all in one platform.
              </p>
            </BoxReveal>

            <BoxReveal boxColor={"transparent"} duration={0.4} width="100%">
              <div className="relative z-10 mx-auto mt-8 flex flex-col items-center gap-4 sm:flex-row lg:mx-0 lg:justify-start">
                <Link href="/download" target="_blank">
                  <Button className="group relative inline-flex h-12 items-center justify-center overflow-hidden rounded-full bg-gradient-to-r from-blue-600 to-purple-600 px-8 font-medium text-white transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-purple-500/25">
                    <span className="relative z-10">Start Your Journey</span>
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                  </Button>
                </Link>

                <div className="text-sm text-neutral-500 dark:text-neutral-400">
                  <WordRotate
                    className="inline font-semibold text-primary"
                    words={[
                      "1,000+ Active Founders",
                      "10+ Startups Launched",
                      "94% Match Rate",
                      "Average 30 Days",
                    ]}
                  />
                </div>
              </div>
            </BoxReveal>

            <BoxReveal boxColor={"transparent"} duration={0.5} width="100%">
              <div className="mx-auto mt-2 flex flex-col items-center sm:flex-row lg:mx-0 lg:justify-start">
                <DownloadAppButtons />
              </div>
            </BoxReveal>
          </div>

          {/* Right Content - iPhone Mockup */}
          <div className="relative flex items-center justify-center">
            <div className="relative">
              {/* Phone Frame */}

              <div className="mx-auto w-[300px]">
                <Iphone videoSrc="https://bucket.cohub42.com/videos/Intro%20Website%20iPhone.mp4" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
